import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import cn from 'classnames';
import { Trans, t } from '@lingui/macro';
import { Authorise, AuthorisedLink, useApplication, useCurrentAgreement } from '@economic/react-app';
import { Header, Icon, Menu } from '@economic/taco';

import { useFeatureFlags } from '~/app/shared/featureFlags';
import { HelpDrawer } from '~/app/components/Header/Help/HelpDrawer';
import * as inapphelpMixpanel from '~/app/components/Header/Help/tracking';
import { CustomTab } from '../../../types';
import { t as tLegacy } from '../../shared/components/l10n/l10n';
import useCachedQuery from '../../shared/hooks/useCachedQuery';
import IntroductionTour from './IntroductionTour/IntroductionTour';
import Quicksearch from './Quicksearch';
import NotificationsIconButton from './Notifications/Button';
import { AgreementDisplay } from './AgreementSelector';
import { useHomeActionProps } from './hooks/useHomeAction';
import Intercom from '../../shared/components/Intercom';

const Link = React.forwardRef(function Link({ active: _, ...props }: any, ref: React.Ref<HTMLAnchorElement>) {
    const location = useLocation();
    const active = location.pathname.startsWith(props.href);

    return <Header.Link {...props} aria-current={active ? 'page' : undefined} ref={ref} />;
});

export default function EconomicHeader({ toggleSidebar, ecopediaLink }) {
    const agreement = useCurrentAgreement();
    const { routes } = useApplication();
    const features = useFeatureFlags();

    // primary navigation
    const homeActionProps = useHomeActionProps();

    const classNames = {
        hidden: 'hidden lg:flex',
        green: '!bg-green-500 hover:!bg-green-300 focus:!bg-green-300',
        yellow: '!bg-yellow-700 hover:!bg-yellow-500 focus:!bg-yellow-500',
    };

    return (
        <>
            <IntroductionTour />
            {/* z-index must be higher than banner, otherwise quicksearch shows behind them */}
            <Header id="header" className="z-[11]" key={Object.keys(routes).length}>
                <Header.MenuButton onClick={toggleSidebar} aria-label="Open menu" />
                <NavLink id="logo" {...homeActionProps}>
                    <Header.Logo />
                </NavLink>
                <PrimaryNavigationLinks as={Header.PrimaryNavigation} id="primary_navigation" linkAs={Link} />
                <Header.SecondaryNavigation id="secondary_navigation">
                    <Authorise path="search">
                        <span className="relative">
                            <Quicksearch className="ml-auto grow-0 shrink-0" />
                        </span>
                    </Authorise>
                    <AuthorisedLink
                        to="inbox"
                        as={Link}
                        className={classNames.hidden}
                        aria-label="Inbox"
                        icon="inbox"
                        tooltip={agreement.modules.smartInbox ? tLegacy`HEADER_TAB_SMARTINBOX` : tLegacy`HEADER_TAB_INBOX`}
                    />
                    <AuthorisedLink
                        to="market"
                        as={Link}
                        className={classNames.hidden}
                        aria-label={tLegacy`HEADER_TAB_MARKET`}
                        icon="market"
                        name="market"
                        tooltip={tLegacy`HEADER_TAB_MARKET`}
                    />
                    <Authorise path="user/notifications">
                        <NotificationsIconButton />
                    </Authorise>
                    {!features.isEnabled('helpDrawer') ? (
                        <>
                            <Authorise path="chat">
                                <Intercom>
                                    {({ toggle }) => (
                                        <Header.Button
                                            icon="chat-solid"
                                            aria-label="Chat"
                                            onClick={() => {
                                                inapphelpMixpanel.trackNavigation({ item: 'chat', placement: 'top-nav' });
                                                toggle();
                                            }}
                                            className={cn(classNames.green, classNames.hidden)}
                                            name="chat"
                                            tooltip={tLegacy`HEADER_TAB_INTERCOM`}
                                        />
                                    )}
                                </Intercom>
                            </Authorise>
                            <AuthorisedLink
                                to={ecopediaLink.url}
                                path="external/e-copedia"
                                as={Header.Link}
                                className={cn(classNames.green, classNames.hidden)}
                                aria-label={ecopediaLink.label}
                                icon="e-copedia"
                                name="ecopedia"
                                target="_blank"
                                tooltip={ecopediaLink.label}
                                onClick={() => inapphelpMixpanel.trackNavigation({ item: 'e-copedia', placement: 'top-nav' })}
                            />
                        </>
                    ) : (
                        <HelpDrawer>
                            <Header.Button
                                aria-label={t`Help`}
                                tooltip={t`Help`}
                                className={classNames.green}
                                icon="question-mark-bold"
                                name="help"
                                onClick={() => inapphelpMixpanel.trackNavigation({ item: 'help', placement: 'top-nav' })}
                            />
                        </HelpDrawer>
                    )}
                    <Header.Button
                        aria-label={tLegacy`HEADER_TAB_SETTINGS`}
                        className={classNames.yellow}
                        icon="settings-solid"
                        menu={menuProps => (
                            <Menu {...menuProps}>
                                <Menu.Content>
                                    <AuthorisedLink to="settings" as={Menu.Link} icon="settings-solid">
                                        <Trans>All settings</Trans>
                                    </AuthorisedLink>
                                    <AuthorisedLink to="user" as={Menu.Link} icon="profile">
                                        <Trans>My settings</Trans>
                                    </AuthorisedLink>
                                    <AuthorisedLink to="logout" as={Menu.Link} icon="log-out">
                                        <Trans>Log off</Trans>
                                    </AuthorisedLink>
                                </Menu.Content>
                            </Menu>
                        )}
                        name="settings"
                        tooltip={tLegacy`HEADER_TAB_SETTINGS`}
                    />
                </Header.SecondaryNavigation>
                <AgreementDisplay />
            </Header>
        </>
    );
}

export const PrimaryNavigationLinks = props => {
    const { as: Tag, linkAs, linkProps = {}, showNesting = false, ...attributes } = props;
    const { routes } = useApplication();
    const customTabsQuery = useCachedQuery<CustomTab[]>('/user/tabs/custom', []);

    const nestingArrow = showNesting ? <Icon name="chevron-right" className="ml-auto -mr-1" /> : null;

    // we have to create the links with if statements like this so that non authorised children aren't passed to the
    // underlying component - it breaks the "more menu" because it can't detect null children until after rendering
    const linkElements = React.useMemo(() => {
        const links: JSX.Element[] = [];

        if (routes.hasOwnProperty('/system')) {
            links.push(
                <AuthorisedLink key="system" to="system" as={linkAs} {...linkProps}>
                    <Trans>System</Trans> {nestingArrow}
                </AuthorisedLink>
            );
        }
        if (routes.hasOwnProperty('/home')) {
            links.push(
                <AuthorisedLink key="home" to="home" as={linkAs} name="home" {...linkProps}>
                    <Trans>Home</Trans>
                </AuthorisedLink>
            );
        }
        if (routes.hasOwnProperty('/basic')) {
            links.push(
                <AuthorisedLink key="basic" to="basic" as={linkAs} {...linkProps}>
                    <Trans>Basic</Trans> {nestingArrow}
                </AuthorisedLink>
            );
        }
        if (routes.hasOwnProperty('/developer')) {
            links.push(
                <AuthorisedLink key="developer" to="developer" as={linkAs} {...linkProps}>
                    <Trans>Apps</Trans>
                </AuthorisedLink>
            );
        }
        if (routes.hasOwnProperty('/partner-api')) {
            links.push(
                <AuthorisedLink keyy="partner-api" to="partner-api" as={linkAs} {...linkProps}>
                    <Trans>Partner API</Trans>
                </AuthorisedLink>
            );
        }
        if (routes.hasOwnProperty('/sales')) {
            links.push(
                <AuthorisedLink key="sales" to="sales" as={linkAs} name="sales" {...linkProps}>
                    <Trans>Sales</Trans> {nestingArrow}
                </AuthorisedLink>
            );
        }
        if (routes.hasOwnProperty('/accounting')) {
            links.push(
                <AuthorisedLink key="accounting" to="accounting" as={linkAs} name="accounting" {...linkProps}>
                    <Trans>Accounting</Trans> {nestingArrow}
                </AuthorisedLink>
            );
        }
        if (routes.hasOwnProperty('/reports')) {
            links.push(
                <AuthorisedLink key="reports" to="reports" as={linkAs} name="reports" {...linkProps}>
                    <Trans>Reports</Trans> {nestingArrow}
                </AuthorisedLink>
            );
        }
        if (routes.hasOwnProperty('/inventory')) {
            links.push(
                <AuthorisedLink key="inventory" to="inventory" as={linkAs} {...linkProps}>
                    <Trans>Inventory</Trans> {nestingArrow}
                </AuthorisedLink>
            );
        }
        if (routes.hasOwnProperty('/projects')) {
            links.push(
                <AuthorisedLink key="projects" to="projects" as={linkAs} {...linkProps}>
                    <Trans>Projects</Trans> {nestingArrow}
                </AuthorisedLink>
            );
        }
        if (routes.hasOwnProperty('/website')) {
            links.push(
                <AuthorisedLink key="website" to="website" as={linkAs} {...linkProps}>
                    <Trans>Website</Trans>
                </AuthorisedLink>
            );
        }
        if (routes.hasOwnProperty('/webshop')) {
            links.push(
                <AuthorisedLink key="webshop" to="webshop" as={linkAs} {...linkProps}>
                    <Trans>Webshop</Trans>
                </AuthorisedLink>
            );
        }
        if (routes.hasOwnProperty('/webshop-plus')) {
            links.push(
                <AuthorisedLink key="webshop-plus" to="webshop-plus" as={linkAs} {...linkProps}>
                    <Trans>Webshop</Trans>
                </AuthorisedLink>
            );
        }
        if (routes.hasOwnProperty('/journal-pro')) {
            links.push(
                <AuthorisedLink key="journal-pro" to="journal-pro" as={linkAs} {...linkProps}>
                    <Trans>Journal Pro</Trans>
                </AuthorisedLink>
            );
        }
        if (routes.hasOwnProperty('/charlie')) {
            links.push(
                <AuthorisedLink key="charlie" to="charlie" as={linkAs} {...linkProps}>
                    <Trans>Charlie</Trans>
                </AuthorisedLink>
            );
        }
        if (customTabsQuery.data?.length) {
            customTabsQuery.data.forEach((customTab, index) => {
                if (routes.hasOwnProperty('/custom')) {
                    links.push(
                        <AuthorisedLink key={index} to={`custom?uri=${customTab.url}`} as={linkAs} {...linkProps}>
                            {customTab.name}
                        </AuthorisedLink>
                    );
                }
            });
        }

        return links;
    }, [customTabsQuery.data?.length]);

    return (
        <Tag {...attributes} key={customTabsQuery.loading}>
            {linkElements}
        </Tag>
    );
};
